import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import styles from "./ImportSection.module.css";
import { lastInputWasKeyboard, watchInputModality } from "./inputModality";
import { useBodyReveal } from "./useBodyReveal";

export type SectionStatus = "locked" | "open" | "collapsed";

type ImportSectionProps = {
  number: number;
  title: string;
  description?: ReactNode;
  descriptionKey?: string;
  status: SectionStatus;
  /** Bumped by the page each time focus should move to this section's heading. */
  focusToken?: number;
  /** Shown at the right of the header row when the section is collapsed. */
  summary?: ReactNode;
  /** The section holds a valid answer: its badge is tinted, and ticked unless it is the last section. */
  valid?: boolean;
  last?: boolean;
  /** Called with true when the section's height starts moving and false when it stops. */
  onMovingChange?: (moving: boolean) => void;
  children?: ReactNode;
};

/** One numbered block of the import page. Closed until the page opens it; moves focus to its heading when asked. */
export function ImportSection({
  number,
  title,
  description,
  descriptionKey,
  status,
  focusToken = 0,
  summary,
  valid = false,
  last = false,
  onMovingChange,
  children,
}: ImportSectionProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const locked = status === "locked";
  const open = status === "open";
  const reveal = useBodyReveal(open);
  const [fadeChanges, setFadeChanges] = useState(false);

  const { moving } = reveal;
  useEffect(() => {
    onMovingChange?.(moving);
  }, [moving, onMovingChange]);

  useEffect(() => {
    setFadeChanges(true);
    watchInputModality();
  }, []);

  useEffect(() => {
    const heading = headingRef.current;
    if (focusToken === 0 || locked || !heading) return;
    heading.dataset.focusRing = lastInputWasKeyboard() ? "on" : "off";
    heading.focus();
  }, [focusToken, locked]);

  const sectionClass = [styles.section, locked ? styles.locked : "", valid ? styles.done : ""]
    .filter(Boolean)
    .join(" ");

  const fadeClass = fadeChanges ? styles.arrives : "";
  const showSummary = status === "collapsed" && Boolean(summary);
  const line = showSummary ? null : description;
  const hasLine = line !== undefined && line !== null && line !== false && line !== "";
  const lineKey = `description:${descriptionKey ?? (typeof description === "string" ? description : "")}`;
  const badgeKey = valid && !last ? "ticked" : "numbered";

  return (
    <section className={sectionClass} aria-labelledby={headingId}>
      <div ref={reveal.frameRef} className={styles.frame} data-moving={String(reveal.moving)}>
        <div ref={reveal.contentRef}>
          <div ref={reveal.headerRef} className={styles.header}>
            <span key={badgeKey} className={`${styles.badge} ${fadeClass}`} aria-hidden="true">
              {valid && !last ? "✓" : number}
            </span>
            <div className={styles.titles}>
              <h2 ref={headingRef} id={headingId} tabIndex={-1} className={styles.title}>
                {title}
              </h2>
              {hasLine && (
                <p key={lineKey} className={`${styles.description} ${fadeClass}`}>
                  {line}
                </p>
              )}
            </div>
            {showSummary && (
              <div key="summary" className={`${styles.summary} ${fadeClass}`}>
                {summary}
              </div>
            )}
          </div>
          {reveal.rendered && (
            <div
              className={styles.reveal}
              data-open={String(open)}
              inert={!open}
              aria-hidden={open ? undefined : true}
            >
              <div className={styles.body}>{children}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
